import { graphql } from '@/lib/graphql';

export const OrganizationSettingsSubscription = graphql(`
  subscription OrganizationSettings($organizationId: Uuid!) {
    organizationsByPk(id: $organizationId) {
      id
      name
      slug
      createdAt
    }
  }
`);

export const UpdateOrganizationMutation = graphql(`
  mutation UpdateOrganization($organizationId: Uuid!, $name: String!, $slug: String!) {
    updateOrganizationsByPk(pkColumns: { id: $organizationId }, _set: { name: $name, slug: $slug }) {
      id
      name
      slug
    }
  }
`);

export const OrganizationBillingSubscription = graphql(`
  subscription OrganizationBilling($organizationId: Uuid!) {
    organizationBilling(where: { organizationId: { _eq: $organizationId } }, limit: 1) {
      id
      status
      trialEndsAt
      currentPeriodStart
      currentPeriodEnd
      stripeCustomerId
      stripeSubscriptionId
      plan {
        id
        name
        description
        basePrice
        includedMinutes
        includedNumbers
      }
    }
  }
`);

export const BillingUsageRecordsSubscription = graphql(`
  subscription BillingUsageRecords($organizationId: Uuid!) {
    usageRecords(where: { organizationId: { _eq: $organizationId } }, orderBy: { billingPeriodStart: DESC }) {
      id
      metricType
      quantity
      billingPeriodStart
      billingPeriodEnd
    }
  }
`);

export const BillingInvoicesSubscription = graphql(`
  subscription BillingInvoices($organizationId: Uuid!) {
    invoices(where: { organizationId: { _eq: $organizationId } }, orderBy: { billingPeriodStart: DESC }, limit: 12) {
      id
      status
      total
      billingPeriodStart
      billingPeriodEnd
      paidAt
    }
  }
`);

export const BusinessHoursSubscription = graphql(`
  subscription BusinessHoursSchedules($organizationId: Uuid!) {
    businessHours(where: { organizationId: { _eq: $organizationId } }, orderBy: { name: ASC }) {
      id
      name
      timezone
      entries
    }
  }
`);

export const CreateBusinessHoursMutation = graphql(`
  mutation CreateBusinessHours($organizationId: Uuid!, $name: String!, $timezone: String!, $entries: Jsonb!) {
    insertBusinessHoursOne(
      object: { organizationId: $organizationId, name: $name, timezone: $timezone, entries: $entries }
    ) {
      id
    }
  }
`);

export const UpdateBusinessHoursMutation = graphql(`
  mutation UpdateBusinessHours($id: Uuid!, $name: String!, $timezone: String!, $entries: Jsonb!) {
    updateBusinessHoursByPk(pkColumns: { id: $id }, _set: { name: $name, timezone: $timezone, entries: $entries }) {
      id
    }
  }
`);

export const DeleteBusinessHoursMutation = graphql(`
  mutation DeleteBusinessHours($id: Uuid!) {
    deleteBusinessHoursByPk(id: $id) {
      id
    }
  }
`);

export const AgentStatusDefinitionsSubscription = graphql(`
  subscription SettingsAgentStatusDefinitions($organizationId: Uuid!) {
    agentStatusDefinitions(where: { organizationId: { _eq: $organizationId } }, orderBy: { displayOrder: ASC }) {
      id
      name
      label
      color
      availableForRouting
      isSystem
      displayOrder
    }
  }
`);

export const CreateAgentStatusDefinitionMutation = graphql(`
  mutation CreateAgentStatusDefinition(
    $organizationId: Uuid!
    $name: String!
    $label: String!
    $color: String!
    $availableForRouting: Boolean!
    $displayOrder: Int!
  ) {
    insertAgentStatusDefinitionsOne(
      object: {
        organizationId: $organizationId
        name: $name
        label: $label
        color: $color
        availableForRouting: $availableForRouting
        isSystem: false
        displayOrder: $displayOrder
      }
    ) {
      id
    }
  }
`);

export const UpdateAgentStatusDefinitionMutation = graphql(`
  mutation UpdateAgentStatusDefinition($id: Uuid!, $changes: AgentStatusDefinitionsSetInput!) {
    updateAgentStatusDefinitionsByPk(pkColumns: { id: $id }, _set: $changes) {
      id
    }
  }
`);

export const DeleteAgentStatusDefinitionMutation = graphql(`
  mutation DeleteAgentStatusDefinition($id: Uuid!) {
    deleteAgentStatusDefinitionsByPk(id: $id) {
      id
    }
  }
`);

export const OrganizationMembersSubscription = graphql(`
  subscription OrganizationMembersList($organizationId: Uuid!) {
    organizationMembers(where: { organizationId: { _eq: $organizationId } }, orderBy: { email: ASC }) {
      id
      email
      role
      status
      joinedAt
      invitedAt
      extensionNumber
      skills
      maxConcurrentCalls
      autoAnswer
      wrapUpTimeSecs
    }
  }
`);

export const UpdateMemberAgentConfigMutation = graphql(`
  mutation UpdateMemberAgentConfig($id: Uuid!, $changes: OrganizationMembersSetInput!) {
    updateOrganizationMembersByPk(pkColumns: { id: $id }, _set: $changes) {
      id
      extensionNumber
      skills
      maxConcurrentCalls
      autoAnswer
      wrapUpTimeSecs
    }
  }
`);

export const WebrtcCredentialsSubscription = graphql(`
  subscription WebrtcCredentials($organizationId: Uuid!) {
    webrtcCredentials(where: { organizationId: { _eq: $organizationId } }, limit: 1) {
      id
      telnyxConnectionId
      webhookUrl
      createdAt
    }
  }
`);

export const AudioFilesSubscription = graphql(`
  subscription SettingsAudioFiles($organizationId: Uuid!) {
    audioFiles(
      where: { organizationId: { _eq: $organizationId }, deletedAt: { _isNull: true } }
      orderBy: { uploadedAt: DESC }
    ) {
      id
      name
      mimeType
      durationSeconds
      uploadedAt
      queues {
        id
        name
      }
    }
  }
`);

export const AudioFileFlowUsageSubscription = graphql(`
  subscription SettingsAudioFileFlowUsage($organizationId: Uuid!) {
    flows(where: { organizationId: { _eq: $organizationId }, deletedAt: { _isNull: true } }) {
      id
      name
      nodes
      currentPublication {
        id
        nodes
      }
    }
  }
`);

export const QueuesSubscription = graphql(`
  subscription SettingsQueues($organizationId: Uuid!) {
    queues(where: { organizationId: { _eq: $organizationId } }, orderBy: { name: ASC }) {
      id
      name
      routingStrategy
      maxWaitTimeSecs
      overflowAction
      overflowQueueId
      ringTimeoutSecs
      deliveryMode
      slaTargetSecs
      holdMusicUrl
      holdMusicAudioFileId
      holdMusicAudioFile {
        id
        name
      }
      announcementIntervalSecs
      announcementMessage
      enabled
    }
  }
`);

export const QueueHoldMusicAudioFilesSubscription = graphql(`
  subscription SettingsQueueHoldMusicAudioFiles($organizationId: Uuid!) {
    audioFiles(
      where: { organizationId: { _eq: $organizationId }, deletedAt: { _isNull: true } }
      orderBy: { name: ASC }
    ) {
      id
      name
    }
  }
`);

export const QueueMembersSubscription = graphql(`
  subscription SettingsQueueMembers($queueId: Uuid!) {
    queueMembers(where: { queueId: { _eq: $queueId } }, orderBy: { priority: ASC }) {
      id
      memberId
      priority
      isEscalation
      member {
        id
        email
        extensionNumber
      }
    }
  }
`);

export const AddQueueMemberMutation = graphql(`
  mutation AddQueueMember($organizationId: Uuid!, $queueId: Uuid!, $memberId: Uuid!) {
    insertQueueMembersOne(
      object: { organizationId: $organizationId, queueId: $queueId, memberId: $memberId, priority: 0 }
    ) {
      id
    }
  }
`);

export const UpdateQueueMemberMutation = graphql(`
  mutation UpdateQueueMember($id: Uuid!, $changes: QueueMembersSetInput!) {
    updateQueueMembersByPk(pkColumns: { id: $id }, _set: $changes) {
      id
    }
  }
`);

export const DeleteQueueMemberMutation = graphql(`
  mutation DeleteQueueMember($id: Uuid!) {
    deleteQueueMembersByPk(id: $id) {
      id
    }
  }
`);

export const CallRecordingsSubscription = graphql(`
  subscription SettingsCallRecordings($organizationId: Uuid!, $limit: Int!, $offset: Int!) {
    callRecordings(
      where: { organizationId: { _eq: $organizationId } }
      orderBy: { startedAt: DESC }
      limit: $limit
      offset: $offset
    ) {
      id
      recordingId
      callControlId
      fromNumber
      toNumber
      direction
      channels
      format
      startedAt
      endedAt
      flow {
        id
        name
      }
    }
  }
`);

export const CallRecordingsCountSubscription = graphql(`
  subscription SettingsCallRecordingsCount($organizationId: Uuid!) {
    callRecordingsAggregate(where: { organizationId: { _eq: $organizationId } }) {
      aggregate {
        count
      }
    }
  }
`);
